import React, { useMemo, useEffect } from 'react';
import { useTranslation } from '@shared/core';
import { Flex, TextV2, ButtonV2, Box } from '@withjoy/joykit';
import { CheckOutline, Close } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog/ResponsiveDialog';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useAdminAccommodationsTelemetry } from '../../Accommodations.telemetry';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

const DIALOG_MAX_WIDTH = pxToRem(482);
const IMAGE = 'https://withjoy.com/assets/public/travel/accommodations-tour/room-block-rate-hotels.png';

interface RoomBlockRatesDialogProps
  extends Readonly<{
    isOpen: boolean;
    hotelCount: number;
    hasVenue?: boolean;
    onCloseDialog: () => void;
    onShowHotelPicks: () => void;
  }> {}

/**
 * Dialog component for displaying room block rates welcome message with hotel suggestions.
 */
export const RoomBlockRatesDialog: React.FC<RoomBlockRatesDialogProps> = props => {
  const { isOpen, hotelCount, hasVenue, onCloseDialog, onShowHotelPicks } = props;
  const { tq } = useTranslation('travelMap');

  const telemetry = useAdminAccommodationsTelemetry();
  const [accommodationsTourTopOfFunnelExperimentEnabled] = useIsFeatureEnabled('accommodationsTourTopOfFunnelExperiment');

  const suggestionAndCta = useMemo<Readonly<{ suggestion: string; cta: string }>>(() => {
    if (hotelCount > 0) {
      return {
        suggestion: tq`Handpicked hotels for you. Manage them in Joy`,
        cta: tq`View Hotel Picks`,
      };
    }
    if (!hasVenue) {
      return {
        suggestion: tq`We will add hotel suggestions near your venue to get you started.`,
        cta: tq`Add Your Venue`,
      };
    }
    return {
      suggestion: tq`Explore hotels and pick the ones that suit you best.`,
      cta: tq`Explore Hotels with Joy Discounts`,
    };
  }, [hasVenue, hotelCount, tq]);

  const benefits = useMemo(
    () => [tq`Help guests save up to 40% on stays`, tq`More flexibility in room choice and dates`, tq`No commitments or penalties`, suggestionAndCta.suggestion],
    [suggestionAndCta, tq],
  );

  const primaryCta = suggestionAndCta.cta;

  const handleShowHotelPicks = useEventCallback(() => {
    telemetry.roomBlockRatesDialogShowHotelPicks();
    onShowHotelPicks();
  });

  const handleCloseDialog = useEventCallback(() => {
    telemetry.onboardingWelcome.enter('Exited Tour');
    onCloseDialog();
  });

  useEffect(() => {
    if (isOpen) {
      telemetry.onboardingWelcome.enter('Tour Seen');
    }
  }, [telemetry, isOpen]);

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={handleCloseDialog}
      hasCloseButton={false}
      hideDivider
      removeBodyPadding
      dialogSize="xl"
      dialogOverrides={{
        Content: {
          props: {
            maxWidth: DIALOG_MAX_WIDTH,
          },
        },
        Body: {
          props: {
            paddingX: { _: 6, sm: 7 },
            paddingY: 0,
            paddingTop: 8,
            paddingBottom: 7,
          },
        },
      }}
    >
      <Flex
        flexDirection="column"
        alignItems="center"
        gap={6}
        position="relative"
      >
        {/* Hotel Image */}
        <Box
          as="img"
          src={IMAGE}
          alt={tq`Room Block Rates Dialog`}
          width="100%"
          maxHeight={pxToRem(180)}
          borderRadius={pxToRem(12)}
          objectFit="cover"
        />

        {/* Header Text */}
        <Flex
          flexDirection="column"
          gap={1}
          alignItems="center"
          textAlign="center"
          width="100%"
        >
          <TextV2
            typographyVariant="hed1"
            color="mono10"
          >
            {tq`Welcome to Your 'Where to Stay' Guide`}
          </TextV2>
          <TextV2
            typographyVariant="hed6"
            color="mono14"
            fontWeight="semibold"
          >
            {accommodationsTourTopOfFunnelExperimentEnabled ? tq`Big savings, zero commitments` : tq`Same hotels. Better rates.`}
          </TextV2>
          <TextV2
            typographyVariant="body1"
            color="mono12"
          >
            {tq`Exclusive hotel rates for Joy hosts and guests.`}
          </TextV2>
        </Flex>

        {/* Benefits List */}
        <Flex
          flexDirection="column"
          gap={3}
          width="100%"
          marginTop={3}
        >
          {benefits.map((text, index) => (
            <Flex
              key={index}
              gap={1}
              alignItems="center"
            >
              <Flex
                width={pxToRem(32)}
                height={pxToRem(32)}
                alignItems="center"
                justifyContent="center"
              >
                <CheckOutline size={24} />
              </Flex>
              <TextV2
                typographyVariant="body1"
                color="mono14"
                flex={1}
              >
                {text}
              </TextV2>
            </Flex>
          ))}
        </Flex>

        {/* Action Button */}
        <ButtonV2
          size="lg"
          variant="outline"
          intent="neutral"
          shape="rounded"
          onClick={handleShowHotelPicks}
          width="100%"
          height={pxToRem(40)}
        >
          {primaryCta}
        </ButtonV2>
      </Flex>

      {/* Close Button */}
      <Flex
        justifyContent="flex-end"
        position="absolute"
        top={5}
        right={5}
      >
        <ButtonV2
          variant="ghost"
          intent="neutral"
          onClick={handleCloseDialog}
          aria-label={tq`Close Room Block Rates Dialog`}
          padding={0}
          width={pxToRem(16)}
          minWidth={pxToRem(16)}
          height={pxToRem(16)}
        >
          <Close size={16} />
        </ButtonV2>
      </Flex>
    </ResponsiveDialog>
  );
};
